"use client";

import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  initialState,
  taskReducer,
  type TaskAction,
  type TaskState,
  type TaskSession,
  type DemoAgent,
  type TaskCategory,
  type TaskResult,
  workingStatuses,
  DEFAULT_USER,
  loadUser,
  saveUser,
  type DemoUser,
  type CompletedTaskSummary,
  type TrainingConsent,
} from "@/lib/app-types";
import {
  planTask,
  confirmTask,
  streamTask,
  getResult,
  rateTask,
  getBookmarks,
  addBookmark,
  removeBookmark,
} from "@/lib/web-client";
import { addTaskId } from "@/lib/task-history-store";
import type { PlanResponse, TaskEvent } from "@/lib/web-client";
import { useBotworkContext } from "@/contexts/BotworkContext";
import { useToast } from "@/components/app/Toast";
import {
  completeEscrow,
  txExplorerUrl,
} from "@/lib/contracts";

const CLIENT_SIDE_ESCROW = process.env.NEXT_PUBLIC_CLIENT_SIDE_ESCROW === "true";

function shortHash(h: string): string {
  return h.length > 10 ? `${h.slice(0, 8)}…${h.slice(-4)}` : h;
}

export type SignInMethod = "wallet" | "gmail" | "facebook" | "apple" | "guest";

// Extended action type that adds UI-only actions not in the reducer.
export type ExtendedTaskAction = TaskAction | { type: "CLEAR_GATE" };

export interface TaskContextValue {
  phase: TaskState;
  task: TaskSession | null;
  workingProgress: number;
  workingStatus: string;
  errorMessage: string | null;
  gateRequired: boolean;
  user: DemoUser;
  loginOpen: boolean;
  dispatch: (action: ExtendedTaskAction) => void;
  /**
   * Approves the current delivered task. Calls rateTask() for the real
   * taskId, then dispatches APPROVE.
   * Returns the tx hash (or null in non-escrow mode).
   */
  approveCurrentTask: (rating: number) => Promise<string | null>;
  confirmBudget: (amount: number) => void;
  openLogin: () => void;
  closeLogin: () => void;
  signIn: (method: SignInMethod) => void;
  resetBalance: () => void;
  finalizeAndReset: () => void;
  toggleBookmark: (agentId: string) => void;
  isBookmarked: (agentId: string) => boolean;
  /** Called by InputBar before SUBMIT_TASK to stash files for planTask. */
  stashAttachments: (files: Array<{ filename: string; mimeType: string; contentBase64: string }>) => void;
  /** The current task ID (for artifact downloads in ResultCard). */
  currentTaskId: string | null;

  // Community-LLM training-data consent
  trainingConsent: TrainingConsent;
  setTrainingConsent: (next: "opted_in" | "opted_out") => void;
}

const TaskContext = createContext<TaskContextValue | null>(null);

export function useTaskContext(): TaskContextValue {
  const ctx = useContext(TaskContext);
  if (!ctx) throw new Error("useTaskContext must be used within TaskProvider");
  return ctx;
}

// ── helpers: map plan agent → DemoAgent ────────────────────────────
function planAgentToDemoAgent(
  a: NonNullable<PlanResponse["plan"]["subtasks"][number]["assignedAgent"]>,
  category: TaskCategory,
): DemoAgent {
  return {
    id: a.id,
    name: a.name,
    avatarInitials: a.name.slice(0, 2).toUpperCase(),
    avatarBg: "#8635F6",
    rating: a.rating ?? 4.5,
    tasksCompleted: 0,
    tags: a.capabilities.slice(0, 3),
    specialties: a.capabilities.slice(0, 4),
    basePrice: a.priceMin ?? 0,
    priceRange: `$${a.priceMin ?? 0}–$${a.priceMax ?? 0}`,
    status: "available",
    description: a.description ?? "",
    categories: [category, "general"].filter(
      (c, i, arr) => arr.indexOf(c) === i,
    ) as TaskCategory[],
    stats: {
      success: `${Math.round((a.rating ?? 4.5) / 5 * 100)}%`,
      avgSpeed: "4m",
      disputes: "0",
    },
    reviews: [],
  };
}

// Dedup agents by id; assignedAgent first (BEST MATCH), then candidateAgents, up to 4
function extractAgentsFromPlan(plan: PlanResponse["plan"], category: TaskCategory): DemoAgent[] {
  const seen = new Set<string>();
  const agents: DemoAgent[] = [];

  for (const subtask of plan.subtasks) {
    // assignedAgent first — becomes index 0 = BEST MATCH
    if (subtask.assignedAgent && !seen.has(subtask.assignedAgent.id)) {
      seen.add(subtask.assignedAgent.id);
      agents.push(planAgentToDemoAgent(subtask.assignedAgent, category));
    }
    // candidateAgents (alternatives)
    for (const candidate of subtask.candidateAgents ?? []) {
      if (seen.has(candidate.id)) continue;
      seen.add(candidate.id);
      agents.push(planAgentToDemoAgent(candidate, category));
      if (agents.length >= 4) break;
    }
    if (agents.length >= 4) break;
  }
  return agents;
}

// Map real TaskResult (web-client shape) to demo TaskResult shape.
// Threads format, rawContent, and the full webResult through so ResultCard
// can delegate to ResultView for rich HTML / code / csv rendering.
function mapWebResultToTaskResult(
  webResult: import("@/lib/web-client").TaskResult,
  fallbackTitle: string,
  downloadFormat?: string | null,
): TaskResult {
  const preview = webResult.preview;
  const summary = preview?.content ?? "Task completed.";
  return {
    title: fallbackTitle,
    summary,
    recommendations: "", // empty — RECOMMENDATIONS section hidden when empty
    files: (webResult.artifacts ?? []).map((a) => ({
      name: a.filename,
      icon: (a.mimeType?.includes("pdf") ? "file-pdf" : "file-xlsx") as "file-pdf" | "file-xlsx",
    })),
    format: preview?.format,
    rawContent: preview?.content,
    downloadFormat: downloadFormat ?? undefined,
    webResult,
  };
}

export function TaskProvider({ children }: { children: ReactNode }) {
  const botwork = useBotworkContext();
  const toast = useToast();
  const [state, rawDispatch] = useReducer(taskReducer, initialState);
  const [user, setUser] = useState<DemoUser>(DEFAULT_USER);
  const [loginOpen, setLoginOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [gateRequired, setGateRequired] = useState(false);

  // Bookmark state — source of truth after mount fetch.
  const [bookmarkedSet, setBookmarkedSet] = useState<Set<string>>(new Set());

  // Real backend refs — all mutable state that does NOT belong in the reducer
  const planIdRef = useRef<string | null>(null);
  const planDataRef = useRef<PlanResponse | null>(null);
  const taskIdRef = useRef<string | null>(null);
  // React state mirror of taskIdRef so consumers (ResultCard) can read it reactively.
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  // Whether current budget-phase entry came from a real plan (vs INSUFFICIENT_BALANCE)
  const planBudgetReadyRef = useRef(false);
  // Store original prompt + agentTier + outputFormat + attachments for re-plan
  const originalPromptRef = useRef<string | null>(null);
  const agentTierRef = useRef<"fast" | "deep" | undefined>(undefined);
  const outputFormatRef = useRef<"html" | "tsx" | "md" | undefined>(undefined);
  const attachmentsRef = useRef<Array<{ filename: string; mimeType: string; contentBase64: string }>>([]);
  // Guards against StrictMode double-invoke and task-identity re-renders
  // firing planTask more than once per submission / per matching entry.
  const plannedSubmitRef = useRef(false);
  // Signature of the last tier|format combo we re-planned for in the matching
  // phase. Blocks StrictMode/re-render double-fire of the re-plan POST while
  // still allowing a genuine re-plan when the user changes tier or format.
  const matchPlanRef = useRef<string | null>(null);

  // Wrapped dispatch: intercepts CLEAR_GATE (UI-only), forwards all others.
  const dispatch = (action: ExtendedTaskAction) => {
    if (action.type === "CLEAR_GATE") {
      setGateRequired(false);
      return;
    }
    rawDispatch(action);
  };

  // Hero → /app handoff: read prompt from sessionStorage on first render.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const prompt = sessionStorage.getItem("bw_hero_prompt");
    if (prompt) {
      // Clear sessionStorage immediately so refresh doesn't re-fire.
      sessionStorage.removeItem("bw_hero_prompt");
      // Clean the URL of any stale ?taskId param.
      history.replaceState(null, "", "/app/");
      // Kick off the full plan flow with the hero's prompt.
      rawDispatch({ type: "SUBMIT_TASK", description: prompt });
    }
  }, []);

  // Hydrate user from localStorage on mount
  useEffect(() => {
    setUser(loadUser());
    setHydrated(true);
  }, []);

  // Persist user whenever it changes (after hydration)
  useEffect(() => {
    if (hydrated) saveUser(user);
  }, [user, hydrated]);

  // Load bookmarks on mount — anon (401) gives empty set, handled in getBookmarks.
  useEffect(() => {
    getBookmarks()
      .then((bms) => {
        const ids = new Set(
          bms.flatMap((b) => [b.agentId, b.agentPeerId].filter((id): id is string => Boolean(id))),
        );
        setBookmarkedSet(ids);
      })
      .catch(() => {
        /* network error — keep empty set */
      });
  }, []);

  // ── Phase: submitted → call planTask ────────────────────────────
  useEffect(() => {
    if (state.phase !== "submitted" || !state.task) return;
    if (plannedSubmitRef.current) return;   // block StrictMode/re-render double-fire
    plannedSubmitRef.current = true;

    const desc = state.task.description;
    let cancelled = false;

    // Store original prompt so the budget-phase effect can re-plan if the user
    // answers a clarification question.
    originalPromptRef.current = desc;
    agentTierRef.current = undefined; // task-machine doesn't expose tier yet

    planTask(desc).then((plan) => {
      if (cancelled) return;
      planIdRef.current = plan.planId;
      planDataRef.current = plan;

      if (plan.analysis.clarificationNeeded && plan.analysis.clarificationQuestion) {
        // Show the clarification question, then advance to budget.
        const category = (plan.analysis.categories?.[0] ?? "general") as TaskCategory;
        rawDispatch({
          type: "ANALYSIS_DONE",
          questions: [{ title: plan.analysis.clarificationQuestion, choices: [] }],
          recommendedBudget: plan.budget,
          category,
        });
        // planBudgetReadyRef stays false until user answers → ANSWER_QUESTION → budget
        planBudgetReadyRef.current = true;
      } else {
        // No clarification — jump to matching via CONFIRM_BUDGET.
        // Pass the category so FormatSelector shows the right options.
        const category = (plan.analysis.categories?.[0] ?? "general") as TaskCategory;
        planBudgetReadyRef.current = true;
        rawDispatch({ type: "CONFIRM_BUDGET", amount: plan.budget, category });
      }
    }).catch((err) => {
      if (cancelled) return;
      const code = (err as { code?: string })?.code;
      if (code === "gate_required") {
        setGateRequired(true);
        rawDispatch({ type: "RESET" });
        return;
      }
      const msg =
        err instanceof Error ? err.message
        : (err as { message?: string })?.message ?? "Failed to analyse task. Please try again.";
      toast.show(msg);
      rawDispatch({ type: "RESET" });
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.task]);

  // Reset the planTask double-fire guards when their phase ends, so a later
  // submission (after RESET) re-plans and a fresh matching pass re-plans.
  // NOT reset in the effects' own cleanup — that would let StrictMode's
  // unmount→remount re-fire the POST, which is exactly what we block.
  useEffect(() => {
    if (state.phase !== "submitted") plannedSubmitRef.current = false;
    if (state.phase !== "matching") matchPlanRef.current = null;
  }, [state.phase]);

  // ── Phase: budget → auto-advance with plan budget ───────────────
  // Fires when we arrive in budget phase after real plan flow.
  // If the user answered a clarification question, re-call planTask with
  // the enriched prompt before advancing.
  useEffect(() => {
    if (state.phase !== "budget" || !planBudgetReadyRef.current) return;
    // Only auto-advance once — clear the flag immediately to prevent re-entry.
    planBudgetReadyRef.current = false;

    const answers = state.task?.answers ?? {};
    const answerText = Object.values(answers).join(" ").trim();
    const originalPrompt = originalPromptRef.current;

    if (answerText && originalPrompt) {
      // User answered a clarification question — re-plan with their answer.
      const enrichedPrompt = `${originalPrompt}\n\nClarification: ${answerText}`;
      let cancelled = false;
      planTask(enrichedPrompt, planDataRef.current?.budget ?? undefined, agentTierRef.current)
        .then((plan) => {
          if (cancelled) return;
          planIdRef.current = plan.planId;
          planDataRef.current = plan;
          const replanCategory = (plan.analysis.categories?.[0] ?? "general") as TaskCategory;
          rawDispatch({ type: "CONFIRM_BUDGET", amount: plan.budget, category: replanCategory });
        })
        .catch((err) => {
          if (cancelled) return;
          const code = (err as { code?: string })?.code;
          if (code === "gate_required") {
            setGateRequired(true);
            rawDispatch({ type: "RESET" });
            return;
          }
          // Re-plan failed — fall back to original plan budget if available.
          const fallback = planDataRef.current?.budget;
          if (fallback == null) {
            toast.show("Failed to re-analyse task. Please try again.");
            rawDispatch({ type: "RESET" });
            return;
          }
          rawDispatch({ type: "CONFIRM_BUDGET", amount: fallback });
        });
      return () => { cancelled = true; };
    }

    // No clarification answer — advance with existing plan budget.
    const budget = planDataRef.current?.budget;
    if (budget == null) {
      toast.show("No plan data available. Please try again.");
      rawDispatch({ type: "RESET" });
      return;
    }
    rawDispatch({ type: "CONFIRM_BUDGET", amount: budget });
  }, [state.phase, state.task?.answers]);

  // ── Phase: matching → re-plan with tier+format, then show agents ──
  useEffect(() => {
    if (state.phase !== "matching" || !state.task) return;

    const task = state.task;
    const category = task.category;
    const tier = task.selectedTier ?? undefined;
    const fmt = task.selectedFormat ?? undefined;
    const prompt = originalPromptRef.current ?? task.description;
    const answers = task.answers ?? {};
    const answerText = Object.values(answers).join(" ").trim();
    const enrichedPrompt = answerText ? `${prompt}\n\nClarification: ${answerText}` : prompt;
    const budget = task.budget ?? planDataRef.current?.budget;
    const attachments = attachmentsRef.current.length > 0 ? attachmentsRef.current : undefined;

    // Store tier + format for use in re-plan
    agentTierRef.current = tier;
    outputFormatRef.current = fmt;

    // Guard against StrictMode/re-render double-fire of the re-plan POST.
    // Keyed on the tier|format signature so a genuine tier/format change still
    // re-plans (the effect deps include selectedTier/selectedFormat), but the
    // same combo never POSTs twice.
    const planSig = `${tier ?? ""}|${fmt ?? ""}`;
    if (matchPlanRef.current === planSig) return;
    matchPlanRef.current = planSig;

    let cancelled = false;

    // Re-plan with tier+format so the plan reflects user's choices.
    planTask(enrichedPrompt, budget, tier, fmt, attachments)
      .then((plan) => {
        if (cancelled) return;
        planIdRef.current = plan.planId;
        planDataRef.current = plan;

        const agents = extractAgentsFromPlan(plan.plan, category);
        // Cosmetic delay so the matching indicator is visible briefly.
        const t = setTimeout(() => {
          if (cancelled) return;
          rawDispatch({ type: "MATCHING_DONE", topAgents: agents });
        }, 800);
        return () => clearTimeout(t);
      })
      .catch((err) => {
        if (cancelled) return;
        const code = (err as { code?: string })?.code;
        if (code === "gate_required") {
          setGateRequired(true);
          rawDispatch({ type: "RESET" });
          return;
        }
        // Fallback: use existing plan data if re-plan fails
        const existingPlan = planDataRef.current;
        if (existingPlan) {
          const agents = extractAgentsFromPlan(existingPlan.plan, category);
          setTimeout(() => {
            if (cancelled) return;
            rawDispatch({ type: "MATCHING_DONE", topAgents: agents });
          }, 800);
        } else {
          toast.show("No plan data for matching. Please try again.");
          rawDispatch({ type: "RESET" });
        }
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.task?.category, state.task?.selectedTier, state.task?.selectedFormat]);

  // ── Phase: working — confirmTask + streamTask ───────────────────
  //
  // If taskIdRef is already set (Hero handoff), skip confirmTask and
  // drain the stream directly.
  //
  // Revisions: no real revise endpoint exists. Fall back to the mock
  // progress simulator for revisionCount > 0 (out of scope per PM plan).
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (state.phase !== "working" || !state.task) return;

    // Revision path: run mock progress simulator (live revise is out of scope).
    if (state.task.revisionCount > 0) {
      const category = state.task.category;
      const durationMs = 3000;
      const ticks = 40;
      const tickMs = durationMs / ticks;
      const statuses = workingStatuses(category);
      let t = 0;
      progressRef.current = setInterval(() => {
        t += 1;
        const pct = Math.min(100, Math.round((t / ticks) * 100));
        const statusIdx = Math.min(statuses.length - 1, Math.floor((t / ticks) * statuses.length));
        rawDispatch({ type: "PROGRESS_TICK", progress: pct, status: statuses[statusIdx] });
        if (t >= ticks) {
          if (progressRef.current) clearInterval(progressRef.current);
          progressRef.current = null;
          if (!state.task) return;
          // Deliver a mock revised result to keep the UX working.
          rawDispatch({
            type: "DELIVER_RESULT",
            result: {
              title: `${state.task.selectedAgent?.name ?? "Agent"} — revised`,
              summary: "Revised per your feedback. The task has been updated.",
              recommendations: "Updated per your feedback.",
              files: [],
            },
          });
        }
      }, tickMs);
      return () => {
        if (progressRef.current) clearInterval(progressRef.current);
        progressRef.current = null;
      };
    }

    // Main path: call confirmTask (unless Hero already has a taskId).
    let cancelled = false;
    let statusInterval: ReturnType<typeof setInterval> | null = null;
    const description = state.task.description;
    const category = state.task.category;
    const statuses = workingStatuses(category);

    // Track SSE events for progress calculation.
    let eventCount = 0;
    const expectedSteps = planDataRef.current?.plan.executionSteps ?? 4;

    rawDispatch({ type: "PROGRESS_TICK", progress: 1, status: statuses[0] });

    const drainStream = async (tid: string) => {
      let stream: AsyncIterable<TaskEvent>;
      try {
        stream = await streamTask(tid);
      } catch (err) {
        if (cancelled) return;
        const msg = (err as { message?: string })?.message ?? "Failed to connect to task stream.";
        toast.show(msg);
        return;
      }

      // Cosmetic keep-alive: rotate status line when SSE events are sparse.
      let statusCycleIdx = 0;
      statusInterval = setInterval(() => {
        if (cancelled || eventCount > 0) return;
        statusCycleIdx = (statusCycleIdx + 1) % statuses.length;
        rawDispatch({ type: "PROGRESS_TICK", progress: 1, status: statuses[statusCycleIdx] });
      }, 1200);

      try {
        for await (const evt of stream) {
          if (cancelled) break;
          eventCount += 1;

          // Update progress from event count vs expected steps.
          const rawPct = Math.min(95, Math.round((eventCount / Math.max(1, expectedSteps)) * 90) + 5);

          // Pick a status line: prefer event message/stage label over rotation.
          let statusLine: string;
          if (evt.message) {
            statusLine = evt.message;
          } else {
            const sIdx = Math.min(statuses.length - 1, Math.floor((rawPct / 100) * statuses.length));
            statusLine = statuses[sIdx];
          }

          if (evt.type === "done") {
            if (statusInterval) { clearInterval(statusInterval); statusInterval = null; }
            rawDispatch({ type: "PROGRESS_TICK", progress: 99, status: "Finalizing…" });
            // Fetch the real result.
            try {
              const webResult = await getResult(tid);
              if (cancelled) return;
              const result = mapWebResultToTaskResult(webResult, description, state.task?.selectedDownloadFormat);
              rawDispatch({ type: "DELIVER_RESULT", result });
            } catch {
              if (cancelled) return;
              // Fallback to a generic result if getResult fails.
              rawDispatch({
                type: "DELIVER_RESULT",
                result: {
                  title: description,
                  summary: "Task completed.",
                  recommendations: "Task completed successfully.",
                  files: [],
                },
              });
            }
            return;
          }

          if (evt.type === "error") {
            if (statusInterval) { clearInterval(statusInterval); statusInterval = null; }
            const errMsg = evt.error ?? evt.message ?? "Task failed. Try again.";
            toast.show(errMsg);
            rawDispatch({ type: "RESET" });
            return;
          }

          if (evt.type === "credits" && typeof evt.balance === "number") {
            // Update local user balance from credits event.
            setUser((u) => ({ ...u, balance: evt.balance as number }));
          }

          rawDispatch({ type: "PROGRESS_TICK", progress: rawPct, status: statusLine });
        }
      } catch {
        if (cancelled) return;
        toast.show("Task stream disconnected. If the task finished, check your history.");
      } finally {
        if (statusInterval) { clearInterval(statusInterval); statusInterval = null; }
      }
    };

    // Call confirmTask to get taskId, then drain stream.
    const planId = planIdRef.current;
    if (!planId) {
      toast.show("No plan found. Please try again.");
      rawDispatch({ type: "RESET" });
      return;
    }

    confirmTask(planId).then(({ taskId }) => {
      if (cancelled) return;
      taskIdRef.current = taskId;
      setCurrentTaskId(taskId);
      addTaskId(taskId);
      void drainStream(taskId);
    }).catch((err) => {
      if (cancelled) return;
      const code = (err as { code?: string })?.code;
      if (code === "gate_required") {
        setGateRequired(true);
        rawDispatch({ type: "RESET" });
        return;
      }
      if (code === "insufficient_credits") {
        const credits = (err as { credits?: number })?.credits ?? 0;
        const required = (err as { required?: number })?.required ?? 0;
        toast.show(
          `Insufficient credits. You have $${credits.toFixed(2)}, need $${required.toFixed(2)}. Top up to continue.`,
        );
        rawDispatch({ type: "RESET" });
        return;
      }
      if (code === "plan_expired") {
        toast.show("Your plan expired. Please submit the task again.");
        rawDispatch({ type: "RESET" });
        return;
      }
      const msg =
        err instanceof Error ? err.message
        : (err as { message?: string })?.message ?? "Failed to confirm task. Please try again.";
      toast.show(msg);
      rawDispatch({ type: "RESET" });
    });

    return () => {
      cancelled = true;
      if (statusInterval) clearInterval(statusInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.task?.revisionCount]);

  // ── Actions ───────────────────────────────────────────────────────
  function confirmBudget(amount: number) {
    if (user.balance < amount) {
      dispatch({ type: "INSUFFICIENT_BALANCE", needed: amount, have: user.balance });
      return;
    }
    setUser((u) => ({ ...u, balance: Number((u.balance - amount).toFixed(2)) }));
    dispatch({ type: "CONFIRM_BUDGET", amount });
  }

  function openLogin() { setLoginOpen(true); }
  function closeLogin() { setLoginOpen(false); }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function signIn(_method: SignInMethod) {
    setUser((u) => ({ ...u, isSignedIn: true, name: u.name || "Demo User" }));
    setLoginOpen(false);
  }
  function resetBalance() {
    setUser((u) => ({ ...u, balance: 100 }));
    dispatch({ type: "CLEAR_ERROR" });
  }

  async function approveCurrentTask(rating: number): Promise<string | null> {
    const tid = taskIdRef.current;
    if (tid) {
      rateTask(tid, rating).catch(() => {
        // Best-effort — don't block or surface errors.
      });
    }

    if (CLIENT_SIDE_ESCROW) {
      const signer = botwork.signer?.ethersSigner;
      if (!signer) throw new Error("Wallet not connected.");
      toast.show("Submitting completion tx…");
      const tx = await completeEscrow(signer, tid ?? "");
      const receipt = await tx.wait();
      toast.show(
        `Payment split 90/5/5. ${shortHash(tx.hash)} → ${txExplorerUrl(tx.hash)}`,
      );
      dispatch({ type: "APPROVE" });
      botwork.refreshBalance?.().catch(() => {});
      return receipt?.hash ?? tx.hash;
    }

    dispatch({ type: "APPROVE" });
    return null;
  }

  function finalizeAndReset() {
    if (state.task && state.task.selectedAgent) {
      const summary: CompletedTaskSummary = {
        id: state.task.id,
        title: state.task.description,
        agentName: state.task.selectedAgent.name,
        cost: state.task.budget ?? 0,
        rating: state.task.rating,
        completedAt: state.task.completedAt ?? Date.now(),
      };
      setUser((u) => {
        const key = summary.title.trim().toLowerCase();
        const deduped = u.taskHistory.filter(
          (h) => h.title.trim().toLowerCase() !== key,
        );
        return {
          ...u,
          taskHistory: [summary, ...deduped].slice(0, 50),
        };
      });
    }
    // Clear real-backend refs on reset.
    planIdRef.current = null;
    planDataRef.current = null;
    taskIdRef.current = null;
    setCurrentTaskId(null);
    planBudgetReadyRef.current = false;
    outputFormatRef.current = undefined;
    attachmentsRef.current = [];
    dispatch({ type: "RESET" });
  }

  function toggleBookmark(agentId: string) {
    const isCurrentlyBookmarked = bookmarkedSet.has(agentId);

    // Optimistic update.
    setBookmarkedSet((prev) => {
      const next = new Set(prev);
      if (isCurrentlyBookmarked) {
        next.delete(agentId);
      } else {
        next.add(agentId);
      }
      return next;
    });

    if (isCurrentlyBookmarked) {
      removeBookmark(agentId).catch(() => {
        // Revert on failure.
        setBookmarkedSet((prev) => {
          const next = new Set(prev);
          next.add(agentId);
          return next;
        });
        toast.show("Failed to remove bookmark. Please try again.");
      });
    } else {
      // Determine if agentId looks like a peerId (starts with 12D3Koo).
      const isPeerId = agentId.startsWith("12D3Koo");
      addBookmark(isPeerId ? { agentPeerId: agentId } : { agentId }).catch((err) => {
        const code = (err as { code?: string })?.code;
        if (code === "http_error" && (err as { status?: number })?.status === 401) {
          // Anon user — revert + show sign-in hint.
          setBookmarkedSet((prev) => {
            const next = new Set(prev);
            next.delete(agentId);
            return next;
          });
          toast.show("Sign in to bookmark");
          return;
        }
        // Other error — revert.
        setBookmarkedSet((prev) => {
          const next = new Set(prev);
          next.delete(agentId);
          return next;
        });
        toast.show("Failed to add bookmark. Please try again.");
      });
    }
  }

  function isBookmarked(agentId: string): boolean {
    return bookmarkedSet.has(agentId);
  }

  function setTrainingConsent(next: "opted_in" | "opted_out") {
    setUser((u) => ({
      ...u,
      trainingConsent: next,
      trainingConsentAt: Date.now(),
    }));
  }

  function stashAttachments(files: Array<{ filename: string; mimeType: string; contentBase64: string }>) {
    attachmentsRef.current = files;
  }

  const value: TaskContextValue = {
    phase: state.phase,
    task: state.task,
    workingProgress: state.workingProgress,
    workingStatus: state.workingStatus,
    errorMessage: state.errorMessage,
    gateRequired,
    user,
    loginOpen,
    dispatch,
    approveCurrentTask,
    confirmBudget,
    openLogin,
    closeLogin,
    signIn,
    resetBalance,
    finalizeAndReset,
    toggleBookmark,
    isBookmarked,
    stashAttachments,
    currentTaskId,
    trainingConsent: user.trainingConsent,
    setTrainingConsent,
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
}

// Re-export types for consumers
export type { DemoAgent };
